import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Check, Ticket } from "lucide-react";

export default function Pricing() {
  const [plans, setPlans] = useState([]);
  const { user } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();

  useEffect(() => { api.get("/settings/public").then(({ data }) => setPlans(data.plans)).catch(() => {}); }, []);

  const choose = (planId) => {
    if (!user || user === false) { navigate(`/connexion?next=/checkout/${planId}`); return; }
    navigate(`/checkout/${planId}`);
  };

  const perks = [t("pricing.p1"), t("pricing.p2"), t("pricing.p3"), t("pricing.p4")];

  return (
    <div className="max-w-6xl mx-auto px-5 py-14 paper-noise min-h-[70vh]">
      <div className="text-center mb-14">
        <span className="text-xs tracking-[0.25em] uppercase font-semibold text-[#E06D53]">{t("pricing.badge")}</span>
        <h1 className="font-serif text-4xl md:text-5xl tracking-tighter text-[#2C2A28] mt-3">{t("pricing.title")}</h1>
        <p className="text-[#5C5854] mt-4 max-w-xl mx-auto">{t("pricing.subtitle")}</p>
      </div>

      <div className="grid md:grid-cols-3 gap-6 items-stretch">
        {plans.map((plan, i) => (
          <motion.div key={plan.id} initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.1 }}
            className={`relative rounded-2xl border p-8 flex flex-col ${plan.highlight ? "bg-[#1D4E89] text-white border-[#1D4E89] shadow-[0_16px_40px_rgba(29,78,137,0.25)]" : "bg-white border-[#E5E0D8]"}`}
            data-testid={`plan-card-${plan.id}`}>
            {plan.highlight && (
              <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#E06D53] text-white text-xs font-semibold uppercase tracking-widest px-4 py-1 rounded-full">{t("pricing.mostChosen")}</span>
            )}
            <Ticket className={`w-8 h-8 mb-4 ${plan.highlight ? "text-[#E9C46A]" : "text-[#E06D53]"}`} />
            <h3 className={`text-xl font-medium ${plan.highlight ? "text-white" : "text-[#2C2A28]"}`}>{plan.name}</h3>
            <div className="mt-4 flex items-end gap-1">
              <span className="font-serif text-5xl">{(plan.amount / 100).toFixed(0)}€</span>
              <span className={`mb-2 text-sm ${plan.highlight ? "text-white/70" : "text-[#8C857B]"}`}>/ {plan.days} {t("pricing.perDays")}</span>
            </div>
            <ul className="mt-6 space-y-3 flex-1">
              {perks.map((p) => (
                <li key={p} className="flex items-start gap-2 text-sm">
                  <Check className={`w-4 h-4 mt-0.5 shrink-0 ${plan.highlight ? "text-[#E9C46A]" : "text-[#2D6A4F]"}`} />
                  <span className={plan.highlight ? "text-white/90" : "text-[#5C5854]"}>{p}</span>
                </li>
              ))}
            </ul>
            <Button onClick={() => choose(plan.id)}
              className={`w-full rounded-full h-11 mt-8 ${plan.highlight ? "bg-[#E06D53] hover:bg-[#C95B42] text-white" : "bg-[#2C2A28] hover:bg-[#1a1918] text-white"}`}
              data-testid={`plan-choose-${plan.id}`}>
              {t("pricing.choose")}
            </Button>
          </motion.div>
        ))}
      </div>
      <p className="text-center text-xs text-[#8C857B] mt-10">{t("pricing.secure")}</p>
    </div>
  );
}
